/**
 * @fileoverview Minimal inline input for quick subtask creation.
 * Ontkoppeld van context, werkt volledig via props voor maximale flexibiliteit.
 */
import { useMemo, useState, useEffect, useRef } from 'react';
import { Box, TextField } from '@mui/material';
import { parseTaskInput } from '@/features/tasks/utils/data/taskParser';
import { TaskInputTokenPreview } from '@/features/tasks/components/forms/TaskInputTokenPreview';
import { useT } from '@/i18n/useT';
import { QUICK_ADD_PLACEHOLDER_INPUT_SX } from '@/components/layout/quickAddStyles';

const CHAR_IGNORE_TIMEOUT_MS = 500;

const TaskInputInline = ({
  initialTitle = '',
  onSave,
  onCancel,
  triggeringKey = null,
  closeOnSave = false,
  placeholder,
  dense = true,
  showTokenPreview = true,
}) => {
  const t = useT();
  const [title, setTitle] = useState(initialTitle);
  const [errorMessage, setErrorMessage] = useState('');
  const inputRef = useRef(null);
  const ignoreChar = useRef(null);
  const isSubmittingRef = useRef(false);

  const parsedPreview = useMemo(() => parseTaskInput(title), [title]);

  useEffect(() => {
    if (inputRef.current?.querySelector('input')) {
      inputRef.current.querySelector('input').focus();
      if (triggeringKey) {
        ignoreChar.current = triggeringKey;
        const timer = setTimeout(() => {
          ignoreChar.current = null;
        }, CHAR_IGNORE_TIMEOUT_MS);
        return () => clearTimeout(timer);
      }
    }
  }, [triggeringKey]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    if (ignoreChar.current && newValue === ignoreChar.current) {
      ignoreChar.current = null;
      setTitle('');
      return;
    }
    setTitle(newValue);
    if (errorMessage) setErrorMessage('');
  };

  const handleKeyDown = async (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      e.stopPropagation();
      const trimmedTitle = title.trim();
      if (trimmedTitle) {
        // We blijven submitten, maar houden de input open
        const parsedData = parseTaskInput(trimmedTitle);
        try {
          setErrorMessage('');
          // Markeer als bezig om handleBlur te blokkeren tijdens opslaan
          isSubmittingRef.current = true;
          await onSave(parsedData);
          setTitle('');
          isSubmittingRef.current = false;

          // In kalender-weergave sluiten we direct, in lijsten blijven we open voor 'rapid entry'
          if (closeOnSave) {
            onCancel();
          }
        } catch (error) {
          isSubmittingRef.current = false;
          setErrorMessage(error.message || t('error.occurred'));
        }
      } else {
        // Lege enter = afsluiten (bestaand gedrag voor 'klaar met invoeren')
        onCancel();
      }
    } else if (e.key === 'Escape') {
      onCancel(true); // Pass true to indicate focus restoration
    }
  };

  const handleBlur = async () => {
    if (isSubmittingRef.current) {
      return;
    }
    const trimmedTitle = title.trim();
    if (trimmedTitle) {
      const parsedData = parseTaskInput(trimmedTitle);
      try {
        isSubmittingRef.current = true;
        await onSave(parsedData);
      } catch (error) {
        console.error('Failed to save on blur:', error);
      } finally {
        isSubmittingRef.current = false;
      }
    }
    onCancel();
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        width: '100%',
        flex: 1,
        minWidth: 0,
      }}
    >
      <TextField
        ref={inputRef}
        fullWidth
        variant="outlined"
        size="small"
        type="text"
        placeholder={placeholder ?? t('taskinputinline.placeholder')}
        value={title}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        error={!!errorMessage}
        helperText={errorMessage}
        sx={{
          ...QUICK_ADD_PLACEHOLDER_INPUT_SX,
          '& .MuiOutlinedInput-root': {
            p: 0,
            color: 'text.primary',
            fontSize: dense ? 'body2.fontSize' : '1rem',
            '& fieldset': { border: 'none' },
            '&:hover fieldset': { border: 'none' },
            '&.Mui-focused fieldset': {
              border: '1px solid',
              borderColor: 'secondary.main',
            },
          },
          '& .MuiOutlinedInput-input': dense
            ? { p: 0 }
            : { p: 0, py: '9px', pl: '6px' },
          '& .MuiFormHelperText-root': {
            color: 'error.main',
            fontSize: '0.75rem',
            mt: 0.5,
            mx: 0,
          },
        }}
      />
      {showTokenPreview ? (
        <TaskInputTokenPreview parsed={parsedPreview} dense={dense} />
      ) : null}
    </Box>
  );
};

export default TaskInputInline;
